import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { LuX } from "react-icons/lu";

import cx from "./cx";

type DialogContextValue = {
  onClose: () => void;
};

const DialogContext = createContext<DialogContextValue>({
  onClose: () => undefined,
});

type DialogProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

// The open dialogs, oldest first. Escape closes only the newest one (e.g. the photo cropper over a form
// dialog), and the page behind stays locked while any of them is open.
const openDialogs: object[] = [];
let pageOverflow = "";

const Dialog = ({ open, onClose, children }: DialogProps) => {
  // The latest onClose, so the effect below runs only when the dialog opens or closes.
  const onCloseRef = useRef(onClose);

  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    const token = {};

    if (openDialogs.length === 0) {
      pageOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    openDialogs.push(token);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        openDialogs[openDialogs.length - 1] === token
      ) {
        onCloseRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      const index = openDialogs.indexOf(token);
      if (index !== -1) openDialogs.splice(index, 1);

      if (openDialogs.length === 0) {
        document.body.style.overflow = pageOverflow;
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <DialogContext.Provider value={{ onClose }}>
      <div
        className="fixed inset-0 z-50 bg-black/80"
        onClick={onClose}
        aria-hidden="true"
      />
      {children}
    </DialogContext.Provider>,
    document.body
  );
};

export type DialogSize = "md" | "lg" | "wide";

const contentBaseClass =
  "fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg";

const contentSizeClass: Record<DialogSize, string> = {
  md: "max-w-md",
  lg: "max-w-lg",
  wide: "max-w-[80vw] max-h-[90vh] overflow-y-auto",
};

type DialogContentProps = React.HTMLAttributes<HTMLDivElement> & {
  size?: DialogSize;
  allowClose?: boolean;
};

const DialogContent = ({
  className,
  size = "lg",
  allowClose = true,
  children,
  ...props
}: DialogContentProps) => {
  const { onClose } = useContext(DialogContext);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cx(contentBaseClass, contentSizeClass[size], className)}
      {...props}
    >
      {children}

      {allowClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <LuX className="h-4 w-4" />
          <span className="sr-only">બંધ કરો</span>
        </button>
      )}
    </div>
  );
};

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cx("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props}
  />
);

const DialogTitle = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2
    className={cx(
      "text-xl font-semibold leading-none tracking-tight text-[#113F67]",
      className
    )}
    {...props}
  >
    {children}
  </h2>
);

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cx("flex gap-2 pt-4 border-t", className)} {...props} />
);

export { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter };
